import { http, unwrapData } from '@noor/api';
import {
  slotEquipmentSchema,
  slotListSchema,
  slotReserveListSchema,
  slotSchema,
  type Slot,
  type SlotEquipment,
  type SlotList,
  type SlotReserveList,
} from '../model/schema';
import type { SlotListFilter, SlotReservesFilter } from '../lib/filters';
import type { SlotRequestBody } from '../lib/payload';

/**
 * Bonus-slot API — the exact cpanel paths, relative to the shared `http` base
 * (`…/api/v1`). Query objects drop empty values (the old `qs` behavior);
 * responses are parsed at the boundary.
 */

export type SlotId = string | number;

/** Equipment options fetched for the "applies to" multiselect. */
const EQUIPMENTS_LIMIT = 100;

function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query[key] = String(value);
    }
  }
  return query;
}

export const slotApi = {
  /** `GET admin/bonus-periods` — one page of slots + the total count. */
  async list(filter: SlotListFilter): Promise<SlotList> {
    const raw = await http.get<unknown>('admin/bonus-periods', {
      query: toQuery(filter),
    });
    return slotListSchema.parse(raw);
  },

  /** `GET admin/bonus-periods/:id` — one slot (the edit form's seed). */
  async get(id: SlotId): Promise<Slot> {
    const raw = await http.get<unknown>(`admin/bonus-periods/${id}`);
    return slotSchema.parse(unwrapData(raw));
  },

  /** `POST admin/bonus-periods`. */
  async create(body: SlotRequestBody): Promise<unknown> {
    return await http.post<unknown>('admin/bonus-periods', body);
  },

  /** `PATCH admin/bonus-periods/:id`. */
  async update(id: SlotId, body: SlotRequestBody): Promise<unknown> {
    return await http.patch<unknown>(`admin/bonus-periods/${id}`, body);
  },

  /** `DELETE admin/bonus-periods/:id`. */
  async remove(id: SlotId): Promise<unknown> {
    return await http.delete<unknown>(`admin/bonus-periods/${id}`);
  },

  /** `GET admin/bonuses/reserves` — the couriers that reserved a slot. */
  async reserves(filter: SlotReservesFilter): Promise<SlotReserveList> {
    const raw = await http.get<unknown>('admin/bonuses/reserves', {
      query: toQuery(filter),
    });
    return slotReserveListSchema.parse(raw);
  },

  /** `GET admin/equipments` — the equipment a slot can be limited to. */
  async equipments(): Promise<SlotEquipment[]> {
    const raw = await http.get<unknown>('admin/equipments', {
      query: { limit: String(EQUIPMENTS_LIMIT) },
    });
    const list: unknown[] =
      typeof raw === 'object' && raw !== null && 'data' in raw && Array.isArray(raw.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : [];
    return list.flatMap((item) => {
      const parsed = slotEquipmentSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    });
  },
};
